import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronRight, ChevronLeft, ChevronsLeftRight, ChevronsRightLeft, Users, Check } from 'lucide-react';
import ModulLayout from '../../layout/ModulLayout';
import ListToolbar, { PohledPill, PohledTlacitko } from '../../layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry } from '../../layout/ListFiltr';
import KDoreseniRadek, { KDoreseniSeznam } from '../../layout/KDoreseni';
import { SECTION_COLOR } from '../lide';
import { Tlacitko, Hlaska, muted, linka } from '../karta/ui';
import ZpetTlacitko from '../../ui/ZpetTlacitko';

/**
 * Lidé → Kvalifikace (dřív Vzdělávání → Kvalifikační matice).
 *
 * Proti staré matici:
 *  - **sloupce jsou kategorie dovedností**, ne 34 dovedností naráz. Sbalená
 *    kategorie ukazuje podíl dovedností na cíli kariéry, rozbalená jednotlivé
 *    dovednosti. Hlavička vysvětluje sloupec názvem, ne hover tooltipem.
 *  - **úroveň = tři sloupky v barvě sekce** (míra, ne stav). Červený rámeček
 *    jen tam, kde je člověk pod cílem své kariéry — dřív svítila zeleně každá
 *    trojka a problém se v tom ztratil (norma §3: barvu nesou dva stavy).
 *  - **editace po člověku** v detailu vedle seznamu, segmentovým ovladačem 0–3
 *    (44 px na mobilu); dřív se cyklovalo klikáním do buňky v editovaném řádku.
 *  - třetí sloupec (`panelVzdy`): pokrytí po kategoriích a „K dořešení";
 *    s vybraným člověkem jeho mezery. Na mobilu řádek „Pod cílem kariéry" → modal.
 */

const UROVNE = ['žádná', 'základní', 'pokročilá', 'expert'];

/** Tři sloupky úrovně. `pod` = pod cílem kariéry (červený rámeček). */
function Sloupky({ uroven = 0, pod = false, velke = false }) {
    const w = velke ? 6 : 5;
    const vysky = velke ? [6, 11, 16] : [5, 9, 13];
    return (
        <span title={`úroveň ${uroven} — ${UROVNE[uroven] || ''}${pod ? ' · pod cílem kariéry' : ''}`}
            style={{
                display: 'inline-flex', alignItems: 'flex-end', gap: 2, padding: 2, borderRadius: 4,
                height: vysky[2] + 4, boxShadow: pod ? '0 0 0 1px rgba(239,68,68,0.75)' : 'none',
            }}>
            {vysky.map((h, i) => (
                <i key={i} style={{ display: 'block', width: w, height: h, borderRadius: 1, background: i < uroven ? SECTION_COLOR : 'rgba(148,163,184,0.22)' }} />
            ))}
        </span>
    );
}

/** Souhrn člověka v kategorii: kolik požadavků kariéry splňuje. */
function souhrnSekce(z, sekce, hodnoty = z.dovednosti) {
    const dov = sekce.dovednosti || [];
    const pozadavky = dov.filter(d => (z.pozadavky?.[d.id] || 0) > 0);
    const naCili = pozadavky.filter(d => (hodnoty?.[d.id] || 0) >= z.pozadavky[d.id]).length;
    const prumer = dov.length ? dov.reduce((s, d) => s + (hodnoty?.[d.id] || 0), 0) / dov.length : 0;
    return { pozadavku: pozadavky.length, naCili, prumer };
}

function mezeryZamestnance(z, sekce, hodnoty = z.dovednosti) {
    return sekce.flatMap(s => (s.dovednosti || [])
        .filter(d => (z.pozadavky?.[d.id] || 0) > (hodnoty?.[d.id] || 0))
        .map(d => ({ sekce: s, d, ma: hodnoty?.[d.id] || 0, cil: z.pozadavky[d.id] })));
}

const pozadavkuCelkem = (z) => Object.values(z.pozadavky || {}).filter(v => v > 0).length;
const bezHodnoceni = (z) => !Object.values(z.dovednosti || {}).some(v => v > 0);

const POHLEDY = [
    { id: 'vsichni', label: 'Všichni', test: () => true },
    { id: 'pod', label: 'Pod cílem kariéry', test: (z, sekce) => mezeryZamestnance(z, sekce).length > 0, upozorneni: true },
    { id: 'bez', label: 'Bez hodnocení', test: (z) => bezHodnoceni(z) },
];

export default function Kvalifikace({ isDark, isMobile, cardStyle, inputStyle, mutedColor, editableIds = 'all', rezervaDole = null }) {
    const [data, setData] = useState({ sekce: [], zamestnanci: [] });
    const [loading, setLoading] = useState(true);
    const [hledani, setHledani] = useState('');
    const [pohled, setPohled] = useState('vsichni');
    const [filtr, setFiltr] = useState({ pozice: '' });
    const [rozbalene, setRozbalene] = useState(() => new Set());
    const [sbaleneSkupiny, setSbaleneSkupiny] = useState(() => new Set());
    const [vybranyId, setVybranyId] = useState(null);

    const nacti = useCallback(() => fetch('/api/dovednosti/matice').then(r => r.json())
        .then(d => { setData({ sekce: d.sekce || [], zamestnanci: d.zamestnanci || [] }); setLoading(false); })
        .catch(() => setLoading(false)), []);
    useEffect(() => { nacti(); }, [nacti]);

    const { sekce, zamestnanci } = data;
    const smiUpravit = (id) => editableIds === 'all' || (Array.isArray(editableIds) && editableIds.includes(id));
    const aktivniPohled = POHLEDY.find(p => p.id === pohled) || POHLEDY[0];

    const filtrSkupiny = useMemo(() => [{
        id: 'pozice', typ: 'dropdown', label: 'Pozice',
        options: [...new Set(zamestnanci.map(z => z.pozice).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'cs')).map(p => ({ value: p, label: p })),
    }], [zamestnanci]);

    const vyfiltrovani = useMemo(() => {
        const q = hledani.trim().toLowerCase();
        return zamestnanci.filter(z => aktivniPohled.test(z, sekce)
            && (!filtr.pozice || z.pozice === filtr.pozice)
            && (!q || [z.cele_jmeno, z.pozice].some(v => (v || '').toLowerCase().includes(q))));
    }, [zamestnanci, sekce, hledani, aktivniPohled, filtr]);

    const skupiny = useMemo(() => {
        const m = new Map();
        for (const z of vyfiltrovani) {
            const k = z.pozice || 'Bez pozice';
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(z);
        }
        return [...m.entries()]
            .sort(([a], [b]) => (a === 'Bez pozice') - (b === 'Bez pozice') || a.localeCompare(b, 'cs'))
            .map(([nazev, lide]) => ({ nazev, lide: lide.sort((a, b) => a.cele_jmeno.localeCompare(b.cele_jmeno, 'cs')) }));
    }, [vyfiltrovani]);

    const vybrany = vybranyId ? zamestnanci.find(z => z.id === vybranyId) : null;

    // ── Souhrn celé matice (panel bez výběru) ──
    const pokryti = useMemo(() => {
        let pozadavku = 0; let naCili = 0;
        const poSekcich = sekce.map(s => {
            let p = 0; let n = 0;
            for (const z of zamestnanci) { const x = souhrnSekce(z, s); p += x.pozadavku; n += x.naCili; }
            pozadavku += p; naCili += n;
            return { s, pozadavku: p, naCili: n };
        });
        return { pozadavku, naCili, poSekcich };
    }, [sekce, zamestnanci]);

    const kDoreseniMatice = useMemo(() => {
        const polozky = zamestnanci
            .map(z => ({ z, mezery: mezeryZamestnance(z, sekce) }))
            .filter(x => x.mezery.length > 0)
            .sort((a, b) => b.mezery.length - a.mezery.length)
            .map(({ z, mezery }) => ({
                id: `z-${z.id}`, problem: true, zamId: z.id,
                titulek: `${z.cele_jmeno} — pod cílem v ${mezery.length} ${mezery.length === 1 ? 'dovednosti' : 'dovednostech'}`,
                popis: z.urovne?.map(u => `${u.kariera}: ${u.uroven}`).join(' · ') || 'bez kariéry',
            }));
        for (const z of zamestnanci) {
            if (!pozadavkuCelkem(z) && !(z.urovne || []).length) {
                polozky.push({ id: `bk-${z.id}`, problem: false, zamId: z.id, titulek: `${z.cele_jmeno} — bez kariéry`, popis: 'nemá cíl, podle kterého by se hodnotil' });
            }
        }
        // Dovednosti, které umí na expertní úrovni méně než dva lidé — riziko na směnách
        for (const s of sekce) {
            const slabe = (s.dovednosti || []).filter(d => zamestnanci.filter(z => (z.dovednosti?.[d.id] || 0) >= 3).length < 2
                && zamestnanci.some(z => (z.pozadavky?.[d.id] || 0) >= 3));
            if (slabe.length) {
                polozky.push({ id: `s-${s.id}`, problem: false, titulek: `${s.nazev}: méně než 2 experti`, popis: slabe.map(d => d.kod).join(', ') });
            }
        }
        return polozky;
    }, [zamestnanci, sekce]);

    const hranice = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';
    const sloupceSekci = sekce.flatMap(s => (rozbalene.has(s.id)
        ? (s.dovednosti || []).map(d => ({ typ: 'dov', s, d }))
        : [{ typ: 'sekce', s }]));
    const grid = {
        display: 'grid', columnGap: 10, alignItems: 'center',
        gridTemplateColumns: `minmax(200px, 1.6fr) ${sloupceSekci.map(c => (c.typ === 'dov' ? '34px' : 'minmax(96px, 1fr)')).join(' ')}`,
    };
    const prepniSekci = (id) => setRozbalene(r => { const n = new Set(r); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const vseRozbalene = sekce.length > 0 && sekce.every(s => rozbalene.has(s.id));

    const seznam = ({ siroky }) => (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0 }}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={mutedColor} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat zaměstnance nebo pozici…', placeholderKratky: 'Hledat' }}
                filtr={(
                    <FiltrTlacitko skupiny={filtrSkupiny} hodnoty={filtr} onChange={(id, v) => setFiltr(f => ({ ...f, [id]: v || '' }))}
                        onReset={() => setFiltr({ pozice: '' })} pocetVysledku={vyfiltrovani.length}
                        siroky={siroky} kompaktni={isMobile} isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
                // Ve zúženém sloupci ovladač místo zalomených pilulek (norma §2)
                pohledy={siroky || isMobile ? null : POHLEDY.map(p => ({ id: p.id, label: p.label, pocet: zamestnanci.filter(z => p.test(z, sekce)).length, upozorneni: p.upozorneni, barva: p.upozorneni ? '#EF4444' : undefined }))}
                pohledAktivni={pohled}
                onPohled={setPohled}
                pohled={!(siroky || isMobile) ? null : POHLEDY.map(p => {
                    const n = zamestnanci.filter(z => p.test(z, sekce)).length;
                    return (
                        <PohledPill key={p.id} label={p.label} pocet={n} aktivni={pohled === p.id} upozorneni={p.upozorneni && n > 0}
                            onClick={() => setPohled(p.id)} barva={SECTION_COLOR} isDark={isDark} mutedColor={mutedColor} kompaktni={isMobile} />
                    );
                })}
                pohledVpravo={siroky ? (
                    <PohledTlacitko Icon={vseRozbalene ? ChevronsRightLeft : ChevronsLeftRight} label={vseRozbalene ? 'Sbalit kategorie' : 'Rozbalit kategorie'}
                        onClick={() => setRozbalene(vseRozbalene ? new Set() : new Set(sekce.map(s => s.id)))} barva={SECTION_COLOR} mutedColor={mutedColor} />
                ) : null}
                aktivniFiltry={(
                    <AktivniFiltry skupiny={filtrSkupiny} hodnoty={filtr} onChange={(id, v) => setFiltr(f => ({ ...f, [id]: v || '' }))}
                        onReset={() => setFiltr({ pozice: '' })} isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
            />

            <div className="overflow-auto" style={{ flex: 1, minHeight: 0, paddingBottom: rezervaDole || undefined }}>
                {loading ? (
                    <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Načítám…</p>
                ) : vyfiltrovani.length === 0 ? (
                    <div className="text-center px-6 py-10">
                        <Users size={26} style={{ color: mutedColor, opacity: 0.5, margin: '0 auto 10px' }} />
                        <p className="text-xs" style={{ color: mutedColor }}>{hledani.trim() ? `Nic nenalezeno pro „${hledani}"` : 'V tomhle pohledu nikdo není.'}</p>
                    </div>
                ) : (
                    <div style={{ minWidth: siroky ? 'min-content' : undefined }}>
                        {siroky && (
                    <div style={{ ...grid, position: 'sticky', top: 0, zIndex: 1, background: isDark ? 'rgba(12,12,16,0.97)' : 'var(--surface-raised)', borderBottom: `1px solid ${hranice}`, padding: '8px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', color: mutedColor, textTransform: 'uppercase', minWidth: 'min-content' }}>
                        <span>Zaměstnanec</span>
                        {sloupceSekci.map(c => (c.typ === 'sekce' ? (
                            <button key={`s-${c.s.id}`} type="button" onClick={() => prepniSekci(c.s.id)} className="flex items-center truncate"
                                title={`${c.s.nazev} — rozbalit ${c.s.dovednosti?.length || 0} dovedností`}
                                style={{ gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, font: 'inherit', padding: 0, textTransform: 'uppercase' }}>
                                <ChevronRight size={13} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                                <span className="truncate">{c.s.nazev}</span>
                            </button>
                        ) : (
                            <button key={`d-${c.d.id}`} type="button" onClick={() => prepniSekci(c.s.id)} title={`${c.d.kod} · ${c.d.nazev} (klik sbalí ${c.s.nazev})`}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, font: 'inherit', padding: 0, textAlign: 'center',
                                    borderBottom: c.s.dovednosti[0].id === c.d.id ? `2px solid ${SECTION_COLOR}` : '2px solid transparent' }}>
                                {c.d.kod}
                            </button>
                        )))}
                    </div>
                        )}

                        {skupiny.map(sk => {
                            const sbalena = sbaleneSkupiny.has(sk.nazev);
                            const Sipka = sbalena ? ChevronRight : ChevronDown;
                            return (
                                <div key={sk.nazev}>
                                    <button type="button" onClick={() => setSbaleneSkupiny(s => { const n = new Set(s); n.has(sk.nazev) ? n.delete(sk.nazev) : n.add(sk.nazev); return n; })}
                                        className="w-full text-left"
                                        style={{
                                            ...(siroky ? grid : { display: 'flex', alignItems: 'center', gap: 8 }),
                                            padding: '0 14px', minHeight: isMobile ? 44 : 38, border: 'none', borderBottom: `1px solid ${hranice}`,
                                            background: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)', cursor: 'pointer', fontSize: 12.5, color: mutedColor,
                                        }}>
                                        <span className="flex items-center min-w-0" style={{ gap: 8, color: 'var(--text-primary)' }}>
                                            <Sipka size={15} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                                            <span className="truncate font-semibold">{sk.nazev}</span>
                                            <span style={{ color: mutedColor, fontWeight: 500 }}>{sk.lide.length}</span>
                                        </span>
                                        {siroky && sloupceSekci.map(c => {
                                            if (c.typ === 'dov') return <span key={`d-${c.d.id}`} />;
                                            let p = 0; let n = 0;
                                            sk.lide.forEach(z => { const x = souhrnSekce(z, c.s); p += x.pozadavku; n += x.naCili; });
                                            return (
                                                <span key={`s-${c.s.id}`} className="tabular-nums" style={{ fontWeight: 600, color: p && n < p ? '#f87171' : mutedColor }}>
                                                    {p ? `${n}/${p} na cíli` : '—'}
                                                </span>
                                            );
                                        })}
                                    </button>
                                    {!sbalena && sk.lide.map(z => (siroky ? (
                                        <RadekMatice key={z.id} z={z} grid={grid} sloupce={sloupceSekci} aktivni={vybranyId === z.id}
                                            onClick={() => setVybranyId(z.id)} isDark={isDark} mutedColor={mutedColor} hranice={hranice} />
                                    ) : (
                                        <RadekUzky key={z.id} z={z} sekce={sekce} aktivni={vybranyId === z.id}
                                            onClick={() => setVybranyId(z.id)} kompaktni={isMobile} mutedColor={mutedColor} hranice={hranice} />
                                    )))}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {siroky && !loading && vyfiltrovani.length > 0 && (
                <div className="flex items-center flex-wrap" style={{ gap: 14, padding: '8px 14px', borderTop: `1px solid ${hranice}`, fontSize: 11.5, color: mutedColor, flexShrink: 0 }}>
                    <span>Úroveň:</span>
                    {UROVNE.map((u, i) => <span key={u} className="flex items-center" style={{ gap: 6 }}><Sloupky uroven={i} />{u}</span>)}
                    <span className="flex items-center" style={{ gap: 6 }}><Sloupky uroven={1} pod />pod cílem kariéry</span>
                    <span style={{ marginLeft: 'auto' }}>kategorie: počet požadavků kariéry na cíli</span>
                </div>
            )}
        </div>
    );

    const panel = () => {
        if (vybrany) {
            const mezery = mezeryZamestnance(vybrany, sekce);
            const celkem = pozadavkuCelkem(vybrany);
            return (
                <div className="rounded-xl" style={{ ...cardStyle, padding: '14px 16px' }}>
                    <p className="text-[10.5px] font-bold uppercase" style={{ color: muted(isDark), letterSpacing: '0.06em' }}>Na cíli kariéry</p>
                    <p className="font-bold tabular-nums" style={{ fontSize: 28, color: 'var(--text-primary)', margin: '2px 0 8px' }}>
                        {celkem - mezery.length} <span style={{ fontSize: 16, color: muted(isDark), fontWeight: 500 }}>/ {celkem}</span>
                    </p>
                    {sekce.map(s => {
                        const x = souhrnSekce(vybrany, s);
                        if (!x.pozadavku) return null;
                        return (
                            <div key={s.id} className="flex items-center justify-between" style={{ minHeight: 32, borderBottom: `1px solid ${linka(isDark)}` }}>
                                <span className="text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>{s.nazev}</span>
                                <span className="text-[12.5px] font-semibold tabular-nums" style={{ color: x.naCili < x.pozadavku ? '#f87171' : 'var(--text-primary)' }}>{x.naCili}/{x.pozadavku}</span>
                            </div>
                        );
                    })}
                    <p className="text-[10.5px] font-bold uppercase" style={{ color: muted(isDark), letterSpacing: '0.06em', margin: '18px 0 6px' }}>K dořešení</p>
                    <KDoreseniSeznam isDark={isDark} prazdne="Všechny požadavky kariéry splněné."
                        polozky={mezery.slice(0, 8).map(m => ({ id: m.d.id, problem: true, titulek: `${m.d.kod} · ${m.d.nazev}`, popis: `${m.sekce.nazev} · má ${m.ma}, cíl ${m.cil}` }))} />
                    {mezery.length > 8 && <p className="text-[12px]" style={{ color: muted(isDark), marginTop: 6 }}>a dalších {mezery.length - 8}</p>}
                </div>
            );
        }
        const pct = pokryti.pozadavku ? Math.round((pokryti.naCili / pokryti.pozadavku) * 100) : null;
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: '14px 16px' }}>
                <p className="text-[10.5px] font-bold uppercase" style={{ color: muted(isDark), letterSpacing: '0.06em' }}>Pokrytí dovedností</p>
                <p className="font-bold tabular-nums" style={{ fontSize: 28, color: 'var(--text-primary)', margin: '2px 0 4px' }}>
                    {pct == null ? '—' : `${pct} %`} <span style={{ fontSize: 13, color: muted(isDark), fontWeight: 500 }}>cílů kariéry splněno</span>
                </p>
                <div style={{ height: 5, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', marginBottom: 10 }}>
                    <div style={{ height: '100%', borderRadius: 3, width: `${pct || 0}%`, background: SECTION_COLOR }} />
                </div>
                {pokryti.poSekcich.filter(x => x.pozadavku).map(x => (
                    <div key={x.s.id} className="flex items-center justify-between" style={{ minHeight: 32, borderBottom: `1px solid ${linka(isDark)}` }}>
                        <span className="text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>{x.s.ikona ? `${x.s.ikona} ` : ''}{x.s.nazev}</span>
                        <span className="text-[12.5px] font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{Math.round((x.naCili / x.pozadavku) * 100)} %</span>
                    </div>
                ))}
                <p className="text-[10.5px] font-bold uppercase" style={{ color: muted(isDark), letterSpacing: '0.06em', margin: '18px 0 6px' }}>K dořešení</p>
                <KDoreseniSeznam isDark={isDark} polozky={kDoreseniMatice.slice(0, 6)}
                    renderAkce={(p) => (p.zamId ? <Tlacitko isDark={isDark} vyska={28} onClick={() => setVybranyId(p.zamId)}>Otevřít</Tlacitko> : null)} />
            </div>
        );
    };

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznam}
                detail={() => (
                    <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                        {vybrany && (
                            <DetailKvalifikace key={vybrany.id} z={vybrany} sekce={sekce} smiUpravit={smiUpravit(vybrany.id)}
                                isDark={isDark} isMobile={isMobile} rezervaDole={isMobile ? rezervaDole : null}
                                onZavrit={() => setVybranyId(null)}
                                onUlozeno={(hodnoty) => setData(d => ({ ...d, zamestnanci: d.zamestnanci.map(x => (x.id === vybrany.id ? { ...x, dovednosti: hodnoty } : x)) }))} />
                        )}
                    </div>
                )}
                panel={panel} />
        </div>
    );
}

function RadekMatice({ z, grid, sloupce, aktivni, onClick, isDark, mutedColor, hranice }) {
    return (
        <button type="button" onClick={onClick} className="w-full text-left"
            style={{
                ...grid, padding: '0 14px', minHeight: 48, border: 'none', borderBottom: `1px solid ${hranice}`,
                borderLeft: `3px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                background: aktivni ? `${SECTION_COLOR}14` : 'transparent', cursor: 'pointer', fontSize: 13,
            }}>
            <span className="min-w-0">
                <span className="block truncate font-semibold" style={{ color: aktivni ? SECTION_COLOR : 'var(--text-primary)' }}>{z.cele_jmeno}</span>
                <span className="block truncate text-[11.5px]" style={{ color: mutedColor }}>{z.urovne?.map(u => `${u.kariera}: ${u.uroven}`).join(' · ') || 'bez kariéry'}</span>
            </span>
            {sloupce.map(c => {
                if (c.typ === 'dov') {
                    const u = z.dovednosti?.[c.d.id] || 0;
                    const cil = z.pozadavky?.[c.d.id] || 0;
                    return <span key={`d-${c.d.id}`} style={{ display: 'flex', justifyContent: 'center' }}><Sloupky uroven={u} pod={cil > u} /></span>;
                }
                const x = souhrnSekce(z, c.s);
                return (
                    <span key={`s-${c.s.id}`} className="flex items-center" style={{ gap: 8 }}>
                        <span style={{ width: 40, height: 4, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', flexShrink: 0 }}>
                            <span style={{ display: 'block', height: '100%', borderRadius: 2, width: `${(x.prumer / 3) * 100}%`, background: SECTION_COLOR }} />
                        </span>
                        <span className="tabular-nums" style={{ fontSize: 12.5, fontWeight: 600, color: x.pozadavku && x.naCili < x.pozadavku ? '#f87171' : 'var(--text-secondary)' }}>
                            {x.pozadavku ? `${x.naCili}/${x.pozadavku}` : x.prumer.toFixed(1).replace('.', ',')}
                        </span>
                    </span>
                );
            })}
        </button>
    );
}

function RadekUzky({ z, sekce, aktivni, onClick, kompaktni, mutedColor, hranice }) {
    const mezery = mezeryZamestnance(z, sekce).length;
    const celkem = pozadavkuCelkem(z);
    return (
        <button type="button" onClick={onClick} className="w-full text-left flex items-center"
            style={{
                gap: 12, padding: '0 14px', minHeight: kompaktni ? 60 : 54, border: 'none', borderBottom: `1px solid ${hranice}`,
                borderLeft: `3px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                background: aktivni ? `${SECTION_COLOR}14` : 'transparent', cursor: 'pointer',
            }}>
            <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold" style={{ fontSize: kompaktni ? 15 : 13, color: aktivni ? SECTION_COLOR : 'var(--text-primary)' }}>{z.cele_jmeno}</span>
                <span className="block truncate" style={{ fontSize: kompaktni ? 12.5 : 11.5, color: mutedColor }}>
                    {z.urovne?.map(u => `${u.kariera}: ${u.uroven}`).join(' · ') || 'bez kariéry'}
                    {mezery > 0 && <> · <span style={{ color: '#f87171', fontWeight: 600 }}>{mezery} pod cílem</span></>}
                </span>
            </span>
            {celkem > 0 && <span className="tabular-nums" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{celkem - mezery}/{celkem}</span>}
        </button>
    );
}

/** Detail člověka: dovednosti po kategoriích, úroveň segmentovým ovladačem 0–3. */
function DetailKvalifikace({ z, sekce, smiUpravit, isDark, isMobile, rezervaDole, onZavrit, onUlozeno }) {
    const [hodnoty, setHodnoty] = useState(() => ({ ...(z.dovednosti || {}) }));
    const [otevrene, setOtevrene] = useState(() => new Set(sekce.filter(s => souhrnSekce(z, s).naCili < souhrnSekce(z, s).pozadavku).map(s => s.id)));
    const [uklada, setUklada] = useState(false);
    const [hlaska, setHlaska] = useState(null);
    const mut = muted(isDark);

    const zmeneno = sekce.some(s => (s.dovednosti || []).some(d => (hodnoty[d.id] || 0) !== (z.dovednosti?.[d.id] || 0)));
    const mezery = mezeryZamestnance(z, sekce, hodnoty);
    const celkem = pozadavkuCelkem(z);

    const ulozit = async () => {
        setUklada(true); setHlaska(null);
        const token = document.querySelector('meta[name="csrf-token"]')?.content;
        const dovednosti = sekce.flatMap(s => (s.dovednosti || []).map(d => ({ dovednost_id: d.id, uroven: hodnoty[d.id] || 0 })));
        try {
            const res = await fetch(`/api/zamestnanci/${z.id}/dovednosti`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ dovednosti }),
            });
            if (res.ok) { onUlozeno({ ...hodnoty }); setHlaska({ typ: 'ok', text: 'Uloženo' }); }
            else setHlaska({ typ: 'chyba', text: 'Nepodařilo se uložit' });
        } catch { setHlaska({ typ: 'chyba', text: 'Nepodařilo se uložit' }); }
        setUklada(false);
    };

    const kDoreseni = mezery.map(m => ({ id: m.d.id, problem: true, titulek: `${m.d.kod} · ${m.d.nazev}`, popis: `${m.sekce.nazev} · má ${m.ma}, cíl ${m.cil}` }));

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div style={{ padding: isMobile ? '6px 12px 12px' : '14px 18px 12px', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`, flexShrink: 0 }}>
                <div className="flex items-center" style={{ gap: 10 }}>
                    {isMobile && (
                        <ZpetTlacitko onClick={onZavrit} popis="Zpět na seznam" sklo isDark={isDark} />
                    )}
                    {!isMobile && <h2 className="font-bold flex-1 truncate" style={{ fontSize: 19, color: 'var(--text-primary)' }}>{z.cele_jmeno}</h2>}
                    {isMobile && <span className="flex-1" />}
                    {smiUpravit && (
                        <Tlacitko isDark={isDark} primarni vyska={isMobile ? 40 : 32} disabled={!zmeneno || uklada} onClick={ulozit}>
                            {uklada ? 'Ukládám…' : zmeneno ? 'Uložit ●' : <><Check size={14} /> Uloženo</>}
                        </Tlacitko>
                    )}
                </div>
                {isMobile && <h2 className="font-bold" style={{ fontSize: 18, color: 'var(--text-primary)', marginTop: 2 }}>{z.cele_jmeno}</h2>}
                <p className="text-[12.5px]" style={{ color: 'var(--text-secondary)', marginTop: 2 }}>
                    {[z.pozice, z.urovne?.map(u => `${u.kariera}: ${u.uroven}`).join(' · '), celkem ? `${celkem - mezery.length} z ${celkem} požadavků kariéry na cíli` : 'bez požadavků kariéry'].filter(Boolean).join(' · ')}
                </p>
                <Hlaska hlaska={hlaska} />
                {isMobile && kDoreseni.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                        <KDoreseniRadek polozky={kDoreseni} titulek="Pod cílem kariéry" isDark={isDark} barva={SECTION_COLOR} />
                    </div>
                )}
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', paddingBottom: rezervaDole ? `calc(12px + ${rezervaDole})` : 16 }}>
                {sekce.map(s => {
                    const x = souhrnSekce(z, s, hodnoty);
                    const otevrena = otevrene.has(s.id);
                    const Sipka = otevrena ? ChevronDown : ChevronRight;
                    return (
                        <div key={s.id}>
                            <button type="button" onClick={() => setOtevrene(o => { const n = new Set(o); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })}
                                className="w-full flex items-center text-left"
                                style={{ gap: 8, minHeight: 44, padding: '0 16px', border: 'none', borderBottom: `1px solid ${linka(isDark)}`, background: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)', cursor: 'pointer' }}>
                                <Sipka size={15} style={{ color: SECTION_COLOR }} />
                                <span className="font-semibold text-[13.5px]" style={{ color: 'var(--text-primary)' }}>{s.ikona ? `${s.ikona} ` : ''}{s.nazev}</span>
                                <span className="text-[12.5px]" style={{ color: x.pozadavku && x.naCili < x.pozadavku ? '#f87171' : mut, fontWeight: 600, marginLeft: 4 }}>
                                    {x.pozadavku ? `${x.naCili}/${x.pozadavku} na cíli` : `${(s.dovednosti || []).length} dovedností`}
                                </span>
                            </button>
                            {otevrena && (s.dovednosti || []).map(d => {
                                const u = hodnoty[d.id] || 0;
                                const cil = z.pozadavky?.[d.id] || 0;
                                const pod = cil > u;
                                return (
                                    <div key={d.id} className="flex items-center" style={{ gap: 12, minHeight: isMobile ? 60 : 48, padding: '6px 16px', borderBottom: `1px solid ${linka(isDark)}` }}>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[13px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{d.kod} · {d.nazev}</p>
                                            <p className="text-[11.5px]" style={{ color: pod ? '#f87171' : mut, fontWeight: pod ? 600 : 400 }}>
                                                {cil ? `cíl kariéry: ${cil} ${UROVNE[cil]}` : 'kariéra nevyžaduje'}
                                                {/* Sdílená dovednost — jedno hodnocení platí pro všechny kariéry, které ji chtějí */}
                                                {(z.pozadavky_kariery?.[d.id]?.length || 0) > 1 && (
                                                    <span style={{ color: mut, fontWeight: 400 }}> · platí pro {z.pozadavky_kariery[d.id].join(' i ')}</span>
                                                )}
                                            </p>
                                        </div>
                                        {smiUpravit ? (
                                            <div role="radiogroup" aria-label={`Úroveň ${d.nazev}`} className="flex"
                                                style={{ borderRadius: 9, overflow: 'hidden', border: `1px solid ${pod ? 'rgba(239,68,68,0.6)' : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}` }}>
                                                {[0, 1, 2, 3].map(v => (
                                                    <button key={v} type="button" role="radio" aria-checked={u === v} title={UROVNE[v]}
                                                        onClick={() => setHodnoty(h => ({ ...h, [d.id]: v }))}
                                                        style={{
                                                            width: isMobile ? 44 : 34, height: isMobile ? 44 : 30, border: 'none', cursor: 'pointer',
                                                            borderLeft: v ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-subtle)'}` : 'none',
                                                            background: u === v ? `${SECTION_COLOR}2e` : 'transparent',
                                                            color: u === v ? SECTION_COLOR : mut, fontWeight: 700, fontSize: 13,
                                                        }}>
                                                        {v}
                                                    </button>
                                                ))}
                                            </div>
                                        ) : <Sloupky uroven={u} pod={pod} velke />}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

